import { useMemo } from 'react';

import type { OwnershipOption } from 'api/data/ownership/models/OwnershipOption';
import type { OwnershipUser } from 'api/data/ownership/models/OwnershipUser';
import { useFetchOwnershipMapping } from 'api/ownership/hooks/useFetchOwnershipMapping';
import type { ProjectRole } from 'api/projectTeamConfigurations/client-projectTeamApi';
import { useProjectTeamByProjectId } from 'api/projectTeamConfigurations/hooks/useProjectTeamByProjectId';
import { useSelectedProjectTemplateNode } from 'state/selectedProject/selectedProjectHooks';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';
import type { OwnershipPropertySettings } from 'state/selectedTemplate/selectedTemplateStore';

import { getOwnershipDisplayName } from './getOwnershipDisplayName';

/**
 * Stable stand-in for "the team hasn't loaded" (or has no roles). A `= []` default in the destructure
 * below would be a *new* array on every render whenever the query has no data — which is exactly the
 * state a failed team fetch leaves it in. That new reference invalidates `resolvedMapping`'s memo,
 * which hands `useOwnershipOptions` a new mapping, which rebuilds its option objects, which makes
 * `OwnershipSelector`'s `setSelected(options.find(...))` effect see a new object every render and set
 * state again — an unbounded render loop for as long as the picker is open.
 */
const NO_ROLES: ProjectRole[] = [];

interface UseOwnershipMappingOptions {
  /**
   * Off where nothing on screen shows an owner — e.g. a grid whose node type has no ownership column —
   * so the page doesn't load a project's whole team roster (names, emails) just to discard it.
   */
  enabled?: boolean;
}

export const useOwnershipMapping = (projectId: string, { enabled = true }: UseOwnershipMappingOptions = {}) => {
  const {
    data: mapping,
    isLoading: isMappingLoading,
    isError: isMappingError
  } = useFetchOwnershipMapping(projectId, enabled);
  // `retryOnMount: false` on both halves, deliberately. This hook has one composite `isError`, so
  // both queries need the same recovery story or the UI can't describe it honestly — and the
  // mapping half is guarded because a re-subscribing OwnershipSelector turned an errored query into
  // hundreds of requests a second. Leaving the team unguarded would keep that door open on the other
  // endpoint. The cost is that a sustained roster outage needs a page refresh to clear; React
  // Query's own `retry` still absorbs transient failures before either query ever errors.
  //
  // Role names come from *this project's* team only. The tenant-wide `useProjectTeams` list would
  // resolve the same names, but hands every project's team to the page to do it.
  const {
    data: projectTeam,
    isLoading: isTeamLoading,
    isError: isTeamError
  } = useProjectTeamByProjectId(projectId, { enabled, retryOnMount: false });
  const projectRoles = projectTeam?.roles ?? NO_ROLES;

  const resolvedMapping = useMemo(() => {
    if (mapping) {
      const roles: ProjectRole[] = [];

      mapping.users
        .flatMap((u) => u.roles)
        .forEach((r: string) => {
          const role = projectRoles.find((x) => x.id.toString() === r);

          if (role && !roles.find((x) => x.id.toString() === role.id.toString())) {
            roles.push(role);
          }
        });

      return {
        ...mapping,

        roles: roles
      };
    }
  }, [mapping, projectRoles]);

  const isLoading = isMappingLoading || isTeamLoading;
  const isError = isMappingError || isTeamError;

  // A fresh object literal on every render would give consumers a new reference even when nothing
  // changed — anything that depends on it (e.g. an effect keyed on this return value) would then fire
  // every render instead of only when the data actually changes.
  return useMemo(() => ({ mapping: resolvedMapping, isLoading, isError }), [resolvedMapping, isLoading, isError]);
};

export const useOwnershipOptions = (projectId: string, templateNodeId: string, templatePropertyId: string) => {
  const { mapping, isLoading, isError } = useOwnershipMapping(projectId);
  const templateNode = useSelectedProjectTemplateNode(templateNodeId);

  const templateProperty = templateNode?.properties.find((p) => p.id === templatePropertyId);

  const options = useMemo<OwnershipOption[]>(() => {
    const resolvedOptions = [] as OwnershipOption[];

    if (mapping !== undefined && templateProperty !== undefined) {
      const propertySettings = templateProperty.settings as OwnershipPropertySettings;
      const propertyValues = propertySettings?.values ?? {};

      const filteredRoles = Object.values(propertyValues)?.flatMap((ptr) => Object.values(ptr));

      const result = [] as OwnershipUser[];

      if (mapping.users.some((u) => u !== undefined)) {
        mapping.users
          .filter((u) => filteredRoles.some((fr) => u.roles.find((r) => r.toString() === fr) !== undefined))
          .forEach((u) => {
            result.push(u);
          });
      }

      result.forEach((u) => {
        const option = {
          ...u,

          title: getOwnershipDisplayName(u),
          value: u.userName,

          roles: []
        } as OwnershipOption;

        if (!u.firstName || !u.lastName) {
          option.avatar = '?';
        }

        u.roles.forEach((r) => {
          const role = mapping.roles?.find((mr) => mr.id.toString() === r.toString());

          option.roles.push(role?.name ?? 'New Role');
        });

        option.roles = option.roles.sort((a, b) => a.toString().localeCompare(b));

        resolvedOptions.push(option);
      });
    }

    return resolvedOptions;
  }, [mapping, templateProperty]);

  // Same reasoning as useOwnershipMapping above — keep the returned object reference stable across
  // renders that don't actually change its contents.
  return useMemo(() => ({ options, isLoading, isError }), [options, isLoading, isError]);
};

export const useOwnershipUser = (projectId: string, userName: string) => {
  const { mapping } = useOwnershipMapping(projectId);

  return mapping?.users?.find((u) => u.userName === userName);
};

export const useSelectedProjectOwnershipMapping = (options?: UseOwnershipMappingOptions) => {
  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId || '');

  return useOwnershipMapping(projectId, options);
};

export const useSelectedProjectOwnershipOptions = (templateNodeId: string, templatePropertyId: string) => {
  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId || '');

  return useOwnershipOptions(projectId, templateNodeId, templatePropertyId);
};

export const useSelectedProjectOwnershipUser = (userName: string) => {
  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId || '');

  return useOwnershipUser(projectId, userName);
};
